import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    ArrowDownUp,
    CornerUpRight,
    FileStack,
    List,
    LockKeyhole,
    LockKeyholeOpen,
    MinusCircle,
    Square,
    SquareCheck,
    Trash2,
    UserCircle
} from "lucide-react";
import { useMenuContext } from "../../context/useMenuContext";
import { useScheduleContext } from "../../context/useScheduleContext";
import { usePermissions } from "../../usePermissions";
import { useIsTemplate } from "../../useIsTemplate";
import { useFilters } from "../../../../../../utils/hooks/useFilters";
import { usePreferences } from "../../../../../Settings/Configuration/hooks/usePreferences";
import { SCHEDULE_PREFERENCE_KEYS } from "../../../constants/scheduleTypes";
import { getResourceUser, getResourceUserId } from "../../../helpers/data/groupedResourceId";

export const useResourceMenu = ({
    openProfileSheet,
    openCopyModal,
    openTransferModal,
    openActionsModal,
    openDeleteModal
}: any) => {
    const { t } = useTranslation();
    const { resource, closeMenu } = useMenuContext();
    const [{ mode }] = useFilters();
    const { events, selectedEvents, dispatch, ctrlShiftS, type } = useScheduleContext();;
    const isTemplate = useIsTemplate();
    const { canCopy, canDelete, canTransfer, canLock, canUnlock, canApprove } = usePermissions();

    const hideUserProfile = usePreferences(`${SCHEDULE_PREFERENCE_KEYS[type]}.hideUserProfile`);

    const rowEvents = useMemo(() => {
        return (mode !== "month" ? events : [])
            .filter((event: any) => event.resourceId === resource?.id)
            .filter((event: any) => event.type !== "unavailable");
    }, [events, resource?.id, mode]);

    const lockedEvents = useMemo(() => {
        return rowEvents.filter((event: any) => event?.locked);
    }, [rowEvents]);

    const unlockedEvents = useMemo(() => {
        return rowEvents.filter((event: any) => !event?.locked);
    }, [rowEvents]);

    const approvedEvents = useMemo(() => {
        return rowEvents?.filter((event: any) => event.status === "approved");
    }, [rowEvents]);

    const unapprovedEvents = useMemo(() => {
        return rowEvents?.filter((event: any) => event.status === "pending");
    }, [rowEvents]);

    const isSelected = useMemo(() => {
        const userSelectedEvents = selectedEvents?.filter(
            (event: any) => event.resourceId === resource?.id
        );
        return userSelectedEvents?.length === rowEvents?.length && rowEvents?.length > 0;
    }, [selectedEvents, rowEvents?.length, resource?.id]);

    const onSelectChange = useCallback(() => {
        if (isSelected) {
            const ids = rowEvents.map((item: any) => item.id);
            const payload = selectedEvents.filter((event: any) => !ids.includes(event.id));
            dispatch({ type: "select-events", payload })
        } else {
            dispatch({ type: "select-events", payload: [...selectedEvents, ...rowEvents] });
        }

        if (!ctrlShiftS) {
            dispatch({ type: "ctrl-shift-s" });
        }
    }, [rowEvents, selectedEvents, dispatch, isSelected, ctrlShiftS]);

    const onListViewChange = useCallback(() => {
        dispatch({ type: "list-view", payload: { type: "row", selected: resource } });
        closeMenu();
    }, [dispatch, closeMenu, resource]);

    return useMemo(() => {
        const menuItems = [
            {
                icon: isSelected ? SquareCheck : Square,
                label: t("select"),
                onChange: onSelectChange,
                hidden: !canCopy || !canDelete || !canTransfer || !canLock || !canUnlock || !canApprove,
                disabled: rowEvents?.length === 0
            },
            {
                icon: UserCircle,
                label: t("profile"),
                onChange: () => {
                    openProfileSheet(getResourceUser(resource));
                    closeMenu();
                },
                hidden: ["home", "my-profile", "profile"].includes(type) || hideUserProfile,
                disabled: false
            },
            {
                icon: List,
                label: t("list-view"),
                onChange: onListViewChange,
                hidden: isTemplate,
                disabled: rowEvents?.length === 0,
            },
            {
                icon: FileStack,
                label: t("copy"),
                onChange: () => {
                    openCopyModal({ user: getResourceUser(resource), type: "row", isTemplate });
                    closeMenu();
                },
                hidden: !canCopy,
                disabled: rowEvents?.length === 0 || lockedEvents?.length === rowEvents?.length,
            },
            {
                icon: ArrowDownUp,
                label: t("transfer"),
                onChange: () => {
                    openTransferModal({ userId: getResourceUserId(resource?.id), type: "row", isTemplate });
                    closeMenu();
                },
                hidden: !canTransfer,
                disabled: rowEvents?.length === 0 || lockedEvents?.length === rowEvents?.length,
            },
            {
                icon: LockKeyhole,
                label: t("lock"),
                onChange: () => {
                    openActionsModal({
                        type: "row",
                        userId: getResourceUserId(resource?.id),
                        action: "lock"
                    });
                    closeMenu();
                },
                hidden: !canLock || isTemplate,
                disabled: rowEvents?.length === 0 || lockedEvents?.length === rowEvents?.length,
            },
            {
                icon: LockKeyholeOpen,
                label: t("unlock"),
                onChange: () => {
                    openActionsModal({
                        type: "row",
                        userId: getResourceUserId(resource?.id),
                        action: "unlock"
                    });
                    closeMenu();
                },
                hidden: !canUnlock || isTemplate,
                disabled: rowEvents?.length === 0 || unlockedEvents?.length === rowEvents?.length,
            },
            {
                icon: CornerUpRight,
                label: t("publish"),
                onChange: () => {
                    openActionsModal({
                        type: "row",
                        userId: getResourceUserId(resource?.id),
                        action: "publish"
                    });
                    closeMenu();
                },
                hidden: !canApprove || isTemplate,
                disabled: rowEvents?.length === 0 || approvedEvents?.length === rowEvents?.length,
            },
            {
                icon: MinusCircle,
                label: t("unpublish"),
                onChange: () => {
                    openActionsModal({
                        type: "row",
                        userId: getResourceUserId(resource?.id),
                        action: "unpublish"
                    });
                    closeMenu();
                },
                hidden: !canApprove || isTemplate,
                disabled: rowEvents?.length === 0 ||
                    lockedEvents?.length === rowEvents?.length ||
                    unapprovedEvents?.length === rowEvents?.length,
            },
            {
                icon: Trash2,
                label: t("delete"),
                onChange: () => {
                    openDeleteModal({
                        userId: getResourceUserId(resource?.id),
                        type: "row",
                        ids: rowEvents?.map((event: any) => event?.id),
                        isTemplate
                    });
                    closeMenu();
                },
                hidden: !canDelete,
                disabled: rowEvents?.length === 0 || lockedEvents?.length === rowEvents?.length,
            },
        ];
        return menuItems.filter((item: any) => !item.hidden);
    }, [
        type,
        isTemplate,
        isSelected,
        canCopy,
        canTransfer,
        canDelete,
        canApprove,
        canLock,
        canUnlock,
        rowEvents,
        lockedEvents,
        approvedEvents,
        unapprovedEvents,
        resource,
        hideUserProfile,
        unlockedEvents?.length,
        openProfileSheet,
        openActionsModal,
        openTransferModal,
        openDeleteModal,
        openCopyModal,
        onListViewChange,
        onSelectChange,
        closeMenu,
        t
    ]);
}
